import apiUrl from '../../../../constants';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import moment from 'moment';
import VueElementLoading from 'vue-element-loading';
import { BasicSelect } from 'vue-search-select';
import Paginate from 'vuejs-paginate';

const AUTH_HEADERS = { authorization: 'authkey xB84JJ89Hd25' };

// HUB-8046: labels for hold_request_status.request_status values.
const REQUEST_STATUS_LABELS = {
  pending: 'Pending',
  accept: 'On hold',
  reject: 'Rejected',
  release: 'Released',
};

// HUB-8238: bulk hold request CSV columns.
// HUB-8277: FM parent columns on bulk CSV.
const BULK_HOLD_COLUMNS = [
  { key: 'lm_hubid', header: 'LM Hub ID' },
  { key: 'lm_hubname', header: 'LM Hub Name' },
  { key: 'fm_parent_hub_id', header: 'FM Parent Hub ID' },
  { key: 'fm_parent_hub_name', header: 'FM Parent Hub Name' },
  { key: 'hold_status', header: 'Hold Status' },
];

function parseBulkHoldCsvPreview(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) {
    throw new Error('CSV must have a header row and at least one data row');
  }
  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  BULK_HOLD_COLUMNS.forEach((col) => {
    if (!headers.includes(col.key)) {
      throw new Error(`Missing required column: ${col.key}`);
    }
  });
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const values = line.split(',').map((v) => v.trim());
    const row = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] !== undefined ? values[idx] : '';
    });
    const parentIdStr = String(row.fm_parent_hub_id || '').trim();
    if (!/^\d+$/.test(parentIdStr) || parseInt(parentIdStr, 10) < 1) {
      throw new Error(`Row ${i + 1}: fm_parent_hub_id must be a positive integer`);
    }
    if (!String(row.fm_parent_hub_name || '').trim()) {
      throw new Error(`Row ${i + 1}: fm_parent_hub_name is required`);
    }
    const token = String(row.hold_status || '').toLowerCase();
    if (token !== 'true' && token !== 'false') {
      throw new Error(`Row ${i + 1}: hold_status must be true or false`);
    }
  }
  return lines.length - 1;
}

function normalizeBulkHoldResultRow(row) {
  if (!row || typeof row !== 'object') {
    return null;
  }
  return {
    csvRowNumber: row.csvRowNumber != null ? row.csvRowNumber : row.csv_row_number,
    lmHubId: row.lmHubId != null ? row.lmHubId : (row.lm_hub_id != null ? row.lm_hub_id : row.lm_hubid),
    lmHubName: row.lmHubName != null ? row.lmHubName : (row.lm_hub_name != null ? row.lm_hub_name : row.lm_hubname),
    fmParentHubId: row.fmParentHubId != null ? row.fmParentHubId : row.fm_parent_hub_id,
    fmParentHubName: row.fmParentHubName != null ? row.fmParentHubName : row.fm_parent_hub_name,
    holdStatus: row.holdStatus != null ? row.holdStatus : row.hold_status,
    action: row.action || '',
    message: row.message || row.Message || '',
    success: row.success != null ? row.success : row.Success,
  };
}

function isBulkHoldRowFailed(row) {
  const success = row.success;
  if (success === false || success === 'false' || success === 0 || success === '0') {
    return true;
  }
  if (success === true || success === 'true' || success === 1 || success === '1') {
    return false;
  }
  return false;
}

function formatBulkHoldStatusToken(holdStatus) {
  if (holdStatus === true || holdStatus === 'true') {
    return 'true';
  }
  if (holdStatus === false || holdStatus === 'false') {
    return 'false';
  }
  return String(holdStatus != null ? holdStatus : '').trim();
}

function buildBulkFailedResultsFromResponse(data) {
  const normalizedResults = (data.results || [])
    .map(normalizeBulkHoldResultRow)
    .filter(Boolean);
  const failedByRow = new Map();
  let anonymousIdx = 0;

  normalizedResults
    .filter(isBulkHoldRowFailed)
    .forEach((row) => {
      if (row.csvRowNumber != null && row.csvRowNumber !== '') {
        failedByRow.set(Number(row.csvRowNumber), row);
      } else {
        failedByRow.set(`row-${anonymousIdx += 1}`, row);
      }
    });

  const validationErrors = Array.isArray(data.validationErrors) ? data.validationErrors : [];
  validationErrors.forEach((err) => {
    const match = /^Row (\d+): (.+)$/.exec(err);
    const rowNum = match ? Number(match[1]) : null;
    const msg = match ? match[2] : err;
    if (rowNum != null) {
      const existing = failedByRow.get(rowNum);
      if (existing) {
        if (!existing.message) {
          existing.message = msg;
        }
        return;
      }
      failedByRow.set(rowNum, {
        csvRowNumber: rowNum,
        lmHubId: '',
        lmHubName: '',
        fmParentHubId: '',
        fmParentHubName: '',
        holdStatus: '',
        action: '',
        message: msg,
        success: false,
      });
      return;
    }
    failedByRow.set(`err-${anonymousIdx += 1}`, {
      csvRowNumber: '',
      lmHubId: '',
      lmHubName: '',
      fmParentHubId: '',
      fmParentHubName: '',
      holdStatus: '',
      action: '',
      message: msg,
      success: false,
    });
  });

  return {
    bulkResults: normalizedResults.length ? normalizedResults : Array.from(failedByRow.values()),
    bulkFailedResults: Array.from(failedByRow.values()),
  };
}

export default {
  name: 'hold-request-tab',
  components: {
    VueElementLoading,
    BasicSelect,
    Paginate,
  },
  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      userEmail: '',
      fmParentOptions: [],
      selectedFmParent: { value: null, text: '' },
      lmRows: [],
      selectedLmHubIds: {},
      lmHoldAllById: {},
      bulkHoldScope: 'cwb',
      errorMessage: '',
      showConfirmModal: false,
      confirmMessage: '',
      // HUB-8238: bulk CSV upload
      isBulkUploading: false,
      bulkSelectedFile: null,
      bulkRemarks: '',
      bulkParseError: '',
      bulkUploadSummary: null,
      bulkResults: [],
      bulkFailedResults: [],
      currentSubTab: 'upload',
      subTabs: [
        { key: 'upload', value: 'Bulk Upload' },
        { key: 'history', value: 'Upload History' },
      ],
      isLoadingHistory: false,
      uploadHistoryRows: [],
      historyPageIndex: 0,
      historyPageSize: 10,
      historyTotalRows: 0,
      historyPageCount: 0,
      showUploadAuditModal: false,
      isLoadingUploadAudit: false,
      uploadAuditRows: [],
      activeUploadFileId: null,
      auditPageIndex: 0,
      auditPageSize: 10,
      auditTotalRows: 0,
      auditPageCount: 0,
    };
  },
  computed: {
    eligibleRows() {
      return this.lmRows.filter((row) => row.canRaiseHold && !row.isOnHold);
    },
    selectedEligibleCount() {
      return this.eligibleRows.filter((row) => this.selectedLmHubIds[row.lmHubId]).length;
    },
    canSubmit() {
      return this.selectedEligibleCount > 0 && !this.isSubmitting && !this.isLoading;
    },
    allOnHold() {
      return (
        this.lmRows.length > 0
        && this.eligibleRows.length === 0
        && this.lmRows.every((row) => row.isOnHold)
      );
    },
    allEligibleSelected() {
      if (!this.eligibleRows.length) {
        return false;
      }
      return this.eligibleRows.every((row) => this.selectedLmHubIds[row.lmHubId]);
    },
    hasFmParents() {
      return this.fmParentOptions.length > 0;
    },
  },
  mounted() {
    const userdetailEncrypt = window.localStorage.getItem('userdetail');
    const bytes = CryptoJS.AES.decrypt(userdetailEncrypt.toString(), 'Key');
    const plaintext = bytes.toString(CryptoJS.enc.Utf8);
    const userdetail = JSON.parse(plaintext);
    this.userEmail = userdetail[0].email;
    this.loadFmParents();
  },
  methods: {
    apiBase() {
      return apiUrl.api_url_hubops;
    },

    resetLmState() {
      this.lmRows = [];
      this.selectedLmHubIds = {};
      this.lmHoldAllById = {};
      this.errorMessage = '';
    },

    async loadFmParents() {
      this.isLoading = true;
      this.errorMessage = '';
      try {
        const res = await axios.get(`${this.apiBase()}listFmParentHubsFromMapping`, {
          headers: AUTH_HEADERS,
        });
        if (res.data && res.data.ResultCode === 100) {
          this.fmParentOptions = (res.data.ResponseData || []).map((r) => ({
            value: r.fmParentHubId,
            text: r.fmParentHubName,
          }));
        } else {
          this.fmParentOptions = [];
          this.errorMessage =
            (res.data && res.data.ReturnMessage) || 'No FM parent hubs configured';
        }
      } catch (err) {
        console.error(err);
        this.fmParentOptions = [];
        this.errorMessage = 'Error loading FM parent hubs';
      } finally {
        this.isLoading = false;
      }
    },

    async onFmParentSelect(option) {
      this.selectedFmParent = option || { value: null, text: '' };
      this.resetLmState();
      if (!this.selectedFmParent.value) {
        return;
      }
      await this.loadLmHubsWithStatus(this.selectedFmParent.value);
    },

    mapRowFromStatus(lmHub) {
      const latest = lmHub.latestHoldRequest || {};
      return {
        lmHubId: lmHub.lmHubId,
        lmHubName: lmHub.lmHubName,
        isOnHold: lmHub.isOnHold,
        canRaiseHold: lmHub.canRaiseHold,
        latestStatus: latest.request_status || '',
        latestRemark: latest.remarks || '',
        latestTimestamp: latest.lastmodifieddate || latest.createddate || '',
        latestBy: latest.lastmodifiedby || latest.createdby || '',
        submitMessage: '',
        submitError: false,
      };
    },

    async loadLmHubsWithStatus(fmParentHubId) {
      this.isLoading = true;
      this.errorMessage = '';
      try {
        const res = await axios.post(
          `${this.apiBase()}getHoldRequestStatusForFmParent`,
          { fmParentHubId },
          { headers: AUTH_HEADERS }
        );
        if (res.data && res.data.ResultCode === 100) {
          const lmHubs = (res.data.ResponseData && res.data.ResponseData.lmHubs) || [];
          this.lmRows = lmHubs.map((hub) => this.mapRowFromStatus(hub));
        } else {
          this.lmRows = [];
          this.errorMessage =
            (res.data && res.data.ReturnMessage) || 'No LM hubs mapped to this FM parent';
        }
      } catch (err) {
        console.error(err);
        this.lmRows = [];
        this.errorMessage = 'Unable to load LM hold status';
      } finally {
        this.isLoading = false;
      }
    },

    isRowSelectable(row) {
      return row.canRaiseHold && !row.isOnHold;
    },

    toggleRow(lmHubId) {
      if (!this.isRowSelectable(this.lmRows.find((r) => r.lmHubId === lmHubId))) {
        return;
      }
      const nextSelected = !this.selectedLmHubIds[lmHubId];
      this.$set(this.selectedLmHubIds, lmHubId, nextSelected);
      if (!nextSelected) {
        this.$delete(this.lmHoldAllById, lmHubId);
      }
    },

    toggleLmHoldAll(lmHubId) {
      if (!this.selectedLmHubIds[lmHubId]) {
        return;
      }
      if (!this.isRowSelectable(this.lmRows.find((r) => r.lmHubId === lmHubId))) {
        return;
      }
      this.$set(this.lmHoldAllById, lmHubId, !this.lmHoldAllById[lmHubId]);
    },

    toggleSelectAllEligible() {
      const selectAll = !this.allEligibleSelected;
      const next = Object.assign({}, this.selectedLmHubIds);
      this.eligibleRows.forEach((row) => {
        if (selectAll) {
          next[row.lmHubId] = true;
        } else {
          delete next[row.lmHubId];
          delete nextHoldAll[row.lmHubId];
        }
      });
      this.selectedLmHubIds = next;
      this.lmHoldAllById = nextHoldAll;
    },

    statusLabel(status) {
      return REQUEST_STATUS_LABELS[status] || '';
    },

    formatIST(gmtString) {
      if (!gmtString) return '';
      const m = moment(gmtString);
      return m.isValid() ? m.format('DD MMM YYYY, HH:mm') : gmtString;
    },

    formatIst(gmtString) {
      if (!gmtString) return '--';
      const m = moment.utc(gmtString);
      return m.isValid() ? m.utcOffset(330).format('DD-MM-YYYY HH:mm:ss') : gmtString;
    },

    onBulkFileChange(e) {
      this.bulkParseError = '';
      this.bulkSelectedFile = null;
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (!file.name.toLowerCase().endsWith('.csv')) {
        this.bulkParseError = 'Only .csv files are accepted';
        return;
      }
      if (file.size > 5242880) {
        this.bulkParseError = 'Max file size is 5 MB';
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          parseBulkHoldCsvPreview(ev.target.result);
          this.bulkSelectedFile = file;
        } catch (err) {
          this.bulkSelectedFile = null;
          this.bulkParseError = err.message;
        }
      };
      reader.readAsText(file);
    },

    clearBulkUploadForm(preserveSummary = false) {
      this.bulkSelectedFile = null;
      this.bulkRemarks = '';
      this.bulkParseError = '';
      if (!preserveSummary) {
        this.bulkUploadSummary = null;
        this.bulkResults = [];
        this.bulkFailedResults = [];
      }
      if (this.$refs.bulkFileInput) {
        this.$refs.bulkFileInput.value = '';
      }
    },

    downloadBulkSampleCsv() {
      // const csv = 'lm_hubid,lm_hubname,hold_status\n12345,PNQ/LM1,true\n67890,DEL/LM3,false\n';
      const csv = 'lm_hubid,lm_hubname,fm_parent_hub_id,fm_parent_hub_name,hold_status\n'
        + '12345,PNQ/LM1,212,PNQ/CHK,true\n'
        + '67890,DEL/LM3,212,PNQ/CHK,false\n';
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hold_request_bulk_sample.csv';
      a.click();
      URL.revokeObjectURL(url);
    },

    async onBulkUploadSubmit() {
      if (!this.bulkSelectedFile || this.bulkParseError || this.isBulkUploading) {
        return;
      }
      this.isBulkUploading = true;
      this.bulkUploadSummary = null;
      this.bulkResults = [];
      this.bulkFailedResults = [];

      const fd = new FormData();
      fd.append('fileData', this.bulkSelectedFile);
      fd.append('fileName', this.bulkSelectedFile.name);
      fd.append('uploadedBy', this.userEmail);
      if (this.bulkRemarks) {
        fd.append('remarks', this.bulkRemarks);
      }
      fd.append('isHoldAll', String(this.bulkHoldScope === 'holdAll'));

      try {
        const res = await axios.post(`${this.apiBase()}uploadBulkHoldRequest`, fd, {
          headers: AUTH_HEADERS,
        });
        const body = res.data || {};
        const data = body.ResponseData || {};
        const failedPayload = buildBulkFailedResultsFromResponse(data);
        this.bulkResults = failedPayload.bulkResults;
        this.bulkFailedResults = failedPayload.bulkFailedResults;

        this.bulkUploadSummary = {
          message: body.ReturnMessage || '',
          totalRows: data.totalRows,
          raiseCount: data.raiseCount,
          releaseCount: data.releaseCount,
          successCount: data.successCount,
          failureCount: data.failureCount || this.bulkFailedResults.length,
        };

        if (body.ResultCode === 100 || body.ResultCode === 102) {
          this.$alertify.success(body.ReturnMessage || 'Upload complete.');
          this.clearBulkUploadForm(true);
        
          if (this.selectedFmParent.value) {
            await this.loadLmHubsWithStatus(this.selectedFmParent.value);
          }
          if (this.currentSubTab === 'history') {
            this.historyPageIndex = 0;
            await this.loadUploadHistory();
          }
        } else {
          this.$alertify.error(body.ReturnMessage || 'Upload failed');
        }
      } catch (err) {
        console.error(err);
        this.$alertify.error('Bulk hold upload failed');
      } finally {
        this.isBulkUploading = false;
      }
    },

    exportBulkFailedRows() {
      if (!this.bulkFailedResults.length) return;
      // Same columns as upload sample (+ error_message for correction context).
      const header = 'lm_hubid,lm_hubname,fm_parent_hub_id,fm_parent_hub_name,hold_status,error_message\n';
      const lines = this.bulkFailedResults.map((r) => [
        r.lmHubId != null && r.lmHubId !== '' ? r.lmHubId : '',
        `"${String(r.lmHubName || '').replace(/"/g, '""')}"`,
        r.fmParentHubId != null && r.fmParentHubId !== '' ? r.fmParentHubId : '',
        `"${String(r.fmParentHubName || '').replace(/"/g, '""')}"`,
        formatBulkHoldStatusToken(r.holdStatus),
        `"${String(r.message || '').replace(/"/g, '""')}"`,
      ].join(','));
      const blob = new Blob([header + lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hold_request_bulk_failed.csv';
      a.click();
      URL.revokeObjectURL(url);
    },

    onSubTabSelect(tabKey) {
      if (this.currentSubTab === tabKey) {
        return;
      }
      this.currentSubTab = tabKey;
      if (tabKey === 'history') {
        this.closeUploadAuditModal();
        this.historyPageIndex = 0;
        this.loadUploadHistory();
      }
    },

    async loadUploadHistory() {
      this.isLoadingHistory = true;
      try {
        const res = await axios.get(`${this.apiBase()}holdRequestUploadHistory`, {
          params: {
            pageIndex: this.historyPageIndex,
            pageSize: this.historyPageSize,
          },
          headers: AUTH_HEADERS,
        });
        if (res.data && res.data.ResultCode === 100) {
          this.uploadHistoryRows = res.data.ResponseData || [];
          const pg = res.data.pagination || {};
          this.historyTotalRows = pg.totalCount != null
            ? pg.totalCount
            : this.uploadHistoryRows.length;
          this.historyPageCount = pg.totalPages || 0;
          if (
            !this.uploadHistoryRows.length
            && this.historyTotalRows > 0
            && this.historyPageIndex > 0
          ) {
            this.historyPageIndex -= 1;
            return this.loadUploadHistory();
          }
          return;
        }
        this.uploadHistoryRows = [];
        this.historyTotalRows = 0;
        this.historyPageCount = 0;
      } catch (err) {
        console.error(err);
        this.uploadHistoryRows = [];
        this.historyTotalRows = 0;
        this.historyPageCount = 0;
      } finally {
        this.isLoadingHistory = false;
      }
    },

    getHistoryPaginationData(pageNum) {
      this.historyPageIndex = pageNum - 1;
      this.loadUploadHistory();
    },

    async loadUploadAudit(uploadFileId) {
      if (this.activeUploadFileId !== uploadFileId) {
        this.auditPageIndex = 0;
        this.activeUploadFileId = uploadFileId;
      }
      this.isLoadingUploadAudit = true;
      this.uploadAuditRows = [];
      this.showUploadAuditModal = true;
      try {
        const res = await axios.get(`${this.apiBase()}holdRequestUploadAudit`, {
          params: {
            uploadFileId,
            pageIndex: this.auditPageIndex,
            pageSize: this.auditPageSize,
          },
          headers: AUTH_HEADERS,
        });
        if (res.data && res.data.ResultCode === 100) {
          this.uploadAuditRows = res.data.ResponseData || [];
          const pg = res.data.pagination || {};
          this.auditTotalRows = pg.totalCount != null
            ? pg.totalCount
            : this.uploadAuditRows.length;
          this.auditPageCount = pg.totalPages || 0;
          if (
            !this.uploadAuditRows.length
            && this.auditTotalRows > 0
            && this.auditPageIndex > 0
          ) {
            this.auditPageIndex -= 1;
            return this.loadUploadAudit(uploadFileId);
          }
          return;
        }
        this.uploadAuditRows = [];
        this.auditTotalRows = 0;
        this.auditPageCount = 0;
        this.$alertify.error((res.data && res.data.ReturnMessage) || 'Unable to load upload rows');
      } catch (err) {
        console.error(err);
        this.uploadAuditRows = [];
        this.auditTotalRows = 0;
        this.auditPageCount = 0;
        this.$alertify.error('Unable to load upload rows');
      } finally {
        this.isLoadingUploadAudit = false;
      }
    },

    getAuditPaginationData(pageNum) {
      this.auditPageIndex = pageNum - 1;
      if (this.activeUploadFileId) {
        this.loadUploadAudit(this.activeUploadFileId);
      }
    },

    closeUploadAuditModal() {
      this.showUploadAuditModal = false;
      this.uploadAuditRows = [];
      this.isLoadingUploadAudit = false;
      this.activeUploadFileId = null;
      this.auditPageIndex = 0;
      this.auditTotalRows = 0;
      this.auditPageCount = 0;
    },

    formatReleasedBagCount(audit) {
      if (!audit || audit.action !== 'RELEASE') {
        return '-';
      }
      if (audit.releasedBagCount === null || audit.releasedBagCount === undefined) {
        return '-';
      }
      return audit.releasedBagCount;
    },
     formatBulkUploadHoldType(file) {
      if (!file) {
        return 'CWB';
      }
      const isHoldForAll = file.isHoldForAll === true || file.isHoldAll === true;
      return isHoldForAll ? 'ALL' : 'CWB';
    },
    
    formatRowStatus(row) {
      if (row.isOnHold) {
        let text = 'Currently on hold';
        if (row.latestTimestamp) {
          text += ` — since ${this.formatIST(row.latestTimestamp)}`;
        }
        if (row.latestBy) {
          text += ` by ${row.latestBy}`;
        }
        return text;
      }
      if (row.latestStatus) {
        let text = 'Previous hold request';
        const label = this.statusLabel(row.latestStatus);
        if (label) {
          text += ` · ${label}`;
        }
        if (row.latestTimestamp) {
          text += ` · ${this.formatIST(row.latestTimestamp)}`;
        }
        if (row.latestBy) {
          text += ` · by ${row.latestBy}`;
        }
        return text;
      }
      if (!row.canRaiseHold && row.latestStatus === 'pending') {
        let text = 'Hold request in progress · Pending';
        if (row.latestTimestamp) {
          text += ` · ${this.formatIST(row.latestTimestamp)}`;
        }
        if (row.latestBy) {
          text += ` · by ${row.latestBy}`;
        }
        return text;
      }
      return '(no prior request)';
    },

    rowStatusClass(row) {
      if (row.isOnHold) {
        return 'status-on-hold';
      }
      if (row.latestStatus === 'reject' || row.submitError) {
        return 'status-rejected';
      }
      if (row.latestStatus === 'pending' && !row.canRaiseHold) {
        return 'status-pending';
      }
      return 'status-neutral';
    },

    confirmBulkRaise() {
      if (!this.canSubmit) {
        return;
      }
      this.confirmMessage = `Raise hold for ${this.selectedEligibleCount} selected LM hub(s)?`;
      this.showConfirmModal = true;
    },

    onConfirmProceed() {
      this.showConfirmModal = false;
      this.confirmMessage = '';
      this.bulkRaiseHold();
    },

    onConfirmCancel() {
      this.showConfirmModal = false;
      this.confirmMessage = '';
    },

    applyRaiseResultToRow(row, result) {
      const responseData = (result.data && result.data.ResponseData) || {};
      const requestStatus = responseData.requestStatus || '';
      const success = result.data && result.data.ResultCode === 100;

      if (success && requestStatus === 'accept') {
        row.isOnHold = true;
        row.canRaiseHold = false;
        row.latestStatus = 'accept';
        row.latestRemark = '';
        row.latestTimestamp = moment().format('DD MMM YYYY, HH:mm');
        row.latestBy = this.userEmail;
        row.submitMessage = '';
        row.submitError = false;
        this.$delete(this.selectedLmHubIds, row.lmHubId);
        this.$delete(this.lmHoldAllById, row.lmHubId);
        return;
      }

      row.latestStatus = requestStatus || row.latestStatus || 'reject';
      row.latestRemark = result.data.ReturnMessage || row.latestRemark;
      row.latestTimestamp = moment().format('DD MMM YYYY, HH:mm');
      row.latestBy = this.userEmail;
      row.submitMessage = result.data.ReturnMessage || 'Hold request failed';
      row.submitError = true;
      row.canRaiseHold = requestStatus !== 'pending';
      row.isOnHold = false;
    },

    async bulkRaiseHold() {
      if (this.isSubmitting) {
        return;
      }
      // HUB-8203: stable PTL slot order — first N rows by name consume capacity.
      // Never use Promise.all on raise calls; bulkRaiseHoldRequest processes
      // sequentially server-side. Any UI fallback must use for…of + await.
      const selectedRows = this.eligibleRows
        .filter((row) => this.selectedLmHubIds[row.lmHubId])
        .sort((a, b) => a.lmHubName.localeCompare(b.lmHubName));
      if (!selectedRows.length) {
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        const res = await axios.post(
          `${this.apiBase()}bulkRaiseHoldRequest`,
          {
            createdby: this.userEmail,
            fmParentHubId: this.selectedFmParent.value,
            fmParentHubName: this.selectedFmParent.text,
            hubs: selectedRows.map((row) => ({
              hubid: row.lmHubId,
              hubname: row.lmHubName,
              isHoldAll: !!this.lmHoldAllById[row.lmHubId],
            })),
          },
          { headers: AUTH_HEADERS }
        );

        if (res.data && res.data.ResponseData && res.data.ResponseData.hubResults) {
          const hubResults = res.data.ResponseData.hubResults;
          hubResults.forEach((hubResult) => {
            const row = this.lmRows.find((r) => r.lmHubId === hubResult.hubid);
            if (!row) {
              return;
            }
            this.applyRaiseResultToRow(row, {
              data: {
                ResultCode: hubResult.resultCode,
                ReturnMessage: hubResult.message,
                ResponseData: { requestStatus: hubResult.requestStatus },
              },
            });
          });
          if (res.data.ResultCode === 101) {
            this.errorMessage = res.data.ReturnMessage || 'All hold requests failed';
          }
        } else {
          this.errorMessage =
            (res.data && res.data.ReturnMessage) || 'Bulk hold request failed';
        }
      } catch (err) {
        console.error(err);
        this.errorMessage = 'Bulk hold request failed';
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};



// WEBPACK FOOTER //
// ./src/views/components/CwbHoldRequest/HoldRequestTab/HoldRequestTab.js